import { useEffect, useState } from 'react';
import type { AccountSecret } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';

export const SECRET_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface SecretEditorDialogProps {
  open: boolean;
  /** null means create. */
  editing: AccountSecret | null;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (values: { name: string; value: string; description: string }) => void;
}

export function SecretEditorDialog({
  open,
  editing,
  busy,
  error,
  onCancel,
  onSubmit,
}: SecretEditorDialogProps) {
  const creating = editing === null;
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setDescription(editing?.description ?? '');
    setValue('');
  }, [open, editing]);

  const nameValid = SECRET_NAME_PATTERN.test(name);
  const canSubmit = creating ? nameValid && value.length > 0 : true;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent data-testid="secret-editor">
        <DialogHeader>
          <DialogTitle>{creating ? 'New secret' : `Edit ${editing?.name}`}</DialogTitle>
          <DialogDescription>
            Secrets are available to everyone on the account and to every agent. The value is
            encrypted at rest and shown only when revealed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="secret-name">Name</Label>
            <Input
              id="secret-name"
              value={name}
              disabled={!creating}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              placeholder="GITHUB_TOKEN"
              data-testid="secret-name-input"
            />
            {creating && name.length > 0 && !nameValid && (
              <p className="text-caption text-destructive" data-testid="secret-name-error">
                Use uppercase letters, digits and underscore, starting with a letter.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="secret-value">Value</Label>
            <Input
              id="secret-value"
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={creating ? '' : 'Leave blank to keep the current value'}
              data-testid="secret-value-input"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="secret-description">Description</Label>
            <Input
              id="secret-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this secret is for"
              data-testid="secret-description-input"
            />
          </div>

          {error && (
            <p className="text-caption text-destructive" data-testid="secret-editor-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ name, value, description })}
            disabled={busy || !canSubmit}
            data-testid="secret-save"
          >
            {creating ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
